import React, { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import it from 'date-fns/locale/it'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { CalendarIcon } from '@heroicons/react/24/outline'
import { collection, query, where, onSnapshot } from 'firebase/firestore'
import { db } from '../../services/firebase'
import {
  formatDateKey,
  parseDateKeyLocal,
  getSlotsPerPitchForDateKey,
  generateHourlyTimes,
  ROLLING_AVAILABILITY_DAYS
} from '../../utils/centerAvailability'

const chipClass =
  'inline-flex items-center rounded-md bg-sport-primary/10 px-2 py-1 text-xs font-medium text-sport-primary dark:bg-sport-primary/20 dark:text-sport-accent'

const formatSlotRangeLabel = (time) => {
  const t = String(time || '').trim()
  const parts = t.split(':')
  const h = Math.min(23, Math.max(0, parseInt(parts[0], 10) || 0))
  const m = Math.min(59, Math.max(0, parseInt(parts[1], 10) || 0))
  const startMinutes = h * 60 + m
  const endMinutes = startMinutes + 60
  const endH = Math.floor((endMinutes % (24 * 60)) / 60)
  const endM = endMinutes % 60
  const start = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  const end = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`
  return `${start}-${end}`
}

/**
 * Same date picker pattern and Italian locale as Create Match (`react-datepicker`, dd/MM/yyyy).
 * Booked slots are excluded per pitch (same rules as admin SlotCalendar).
 *
 * @param {boolean} compact - Smaller field (for list cards / dialog)
 * @param {boolean} showHeading - Show section title (hide when title is in parent dialog)
 */
const CenterAvailabilityCalendar = ({ center, compact = false, showHeading = true }) => {
  const { t } = useTranslation()

  const sportLabel = (sport) => {
    if (sport === 'calcio') return t('sports.calcio')
    if (sport === 'padel') return t('sports.padel')
    return sport || ''
  }
  /** Italian calendar (weeks start Monday) for center availability; matches site `lang="it"`. */
  const dfLocale = it
  const dateLocale = it

  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [bookings, setBookings] = useState([])

  const hasHours = useMemo(
    () => generateHourlyTimes(center?.openingTime, center?.closingTime).length > 0,
    [center?.openingTime, center?.closingTime]
  )

  useEffect(() => {
    setSelectedDate(new Date())
  }, [center?.id, center?.openingTime, center?.closingTime])

  useEffect(() => {
    if (!center?.id) {
      setBookings([])
      return undefined
    }
    const q = query(collection(db, 'bookings'), where('centerId', '==', center.id))
    const unsub = onSnapshot(
      q,
      (snap) => {
        setBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      },
      () => setBookings([])
    )
    return () => unsub()
  }, [center?.id])

  const selectedKey = formatDateKey(selectedDate)
  const slotsByPitch = useMemo(
    () =>
      hasHours && center ? getSlotsPerPitchForDateKey(center, selectedKey, new Date(), bookings) : [],
    [center, hasHours, selectedKey, bookings]
  )

  const minDate = new Date()
  minDate.setHours(0, 0, 0, 0)
  const maxDate = new Date(minDate)
  maxDate.setDate(maxDate.getDate() + ROLLING_AVAILABILITY_DAYS - 1)

  const filterDate = (date) => {
    if (!date || !hasHours) return false
    const ds = new Date(date)
    ds.setHours(0, 0, 0, 0)
    return ds >= minDate && ds <= maxDate
  }

  if (!hasHours) return null

  return (
    <div className="space-y-3">
      {showHeading ? (
        <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
          {t('centers.availabilityCalendarTitle')}
        </p>
      ) : null}

      <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-2 dark:border-gray-600 dark:bg-gray-800/50">
        <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300">
          {t('createMatch.dateLabel')}
        </label>
        <div className="relative gaming-picker-field">
          <CalendarIcon
            className={`pointer-events-none absolute text-sport-primary z-10 ${compact ? 'left-2.5 top-2.5 h-4 w-4' : 'left-3 top-3 h-5 w-5'}`}
          />
          <DatePicker
            selected={selectedDate}
            onChange={(date) => {
              if (date) setSelectedDate(date)
            }}
            dateFormat="dd/MM/yyyy"
            placeholderText={t('createMatch.datePlaceholder')}
            minDate={minDate}
            maxDate={maxDate}
            filterDate={filterDate}
            locale={dateLocale}
            calendarStartDay={1}
            popperPlacement="bottom-start"
            portalId="datepicker-portal-root"
            className={`gaming-picker-input input-modern w-full pl-10 ${compact ? 'py-2 text-sm' : ''}`}
            calendarClassName="gaming-calendar"
            popperClassName="gaming-picker-popper"
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-800/80">
        <p className="mb-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
          {parseDateKeyLocal(selectedKey)
            ? format(parseDateKeyLocal(selectedKey), 'EEEE d MMMM yyyy', { locale: dfLocale })
            : selectedKey}
        </p>
        {slotsByPitch.length > 0 ? (
          <>
            <p className="mb-3 text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {t('centers.availableSlotsByCourt')}
            </p>
            <div className="space-y-4">
              {slotsByPitch.map((row, index) => {
                const key =
                  row.pitchId != null ? String(row.pitchId) : `center-aggregate-${index}`
                const title =
                  row.pitchId != null
                    ? row.pitchName?.trim()
                      ? row.pitchName
                      : t('centers.pitchUnnamed')
                    : t('centers.availabilityCenterWide')
                const sportSuffix = row.sport ? ` · ${sportLabel(row.sport)}` : ''

                return (
                  <div key={key}>
                    <p className="mb-1.5 text-xs font-semibold text-gray-800 dark:text-gray-200">
                      {title}
                      {sportSuffix}
                    </p>
                    {row.times.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {row.times.map((time) => (
                          <span key={time} className={chipClass}>
                            {formatSlotRangeLabel(time)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('centers.noFreeSlotsForPitch')}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('centers.noSlotsThisDay')}</p>
        )}
        <p className="mt-3 text-[11px] leading-snug text-gray-400 dark:text-gray-500">{t('centers.typicalSlotsHint')}</p>
      </div>
    </div>
  )
}

export default CenterAvailabilityCalendar
